import React, {useContext, useMemo} from "react";
import StyleColourPicker from "./Styles/StyleColourPicker";
import {getDefaultColour} from "./Styles/ManageColours";
import {ClassStylesEditorContext} from "./ContentStylesEditor";

export const resolveVariant = (target, variant) => {
    if (!target) return target;

    if (typeof target === 'string') {
        return target.replace('@', variant).replace('*', variant);
    }

    if (Array.isArray(target)) {
        return target.map(item => resolveVariant(item, variant));
    }

    if (typeof target === 'object') {
        const resolved = { ...target };
        if (resolved.cssVar) {
            resolved.cssVar = resolveVariant(resolved.cssVar, variant);
        }
        if (resolved.title) {
            resolved.title = resolveVariant(resolved.title, variant);
        }
        return resolved;
    }

    return target;
};

export const ContentStyleEditGroup = props => {
    const {editType, variant, children} = props;

    const resolvedEditType = (variant && typeof editType === 'string')
        ? editType.replace('*', variant)
        : editType;

    return (
        <div
            className="ch-s_ed-option--group"
            data-editor-for={resolvedEditType}
        >
            {children}
        </div>
    )
}

export const ContentStyleEditBlock = props => {
    const {editType, variant, children} = props;

    const resolvedEditType = (variant && typeof editType === 'string')
        ? editType.replace('*', variant)
        : editType;

    return (
        <div
            className="ch-s_ed-option--block"
            data-editor-for={resolvedEditType}
        >
            {children}
        </div>
    )
}

export const ContentStylesColourBlock = (props) => {
    const {
        editType,
        variant = null,
        colourConfig,
        colours,
        onChange
    } = props;

    const {styles} = useContext(ClassStylesEditorContext);
    const title = useMemo(() => resolveVariant(colourConfig.title, variant), [colourConfig.title, variant]);
    const resolvedVars = useMemo(() => {
        const rawVars = Array.isArray(colourConfig.cssVar) ? colourConfig.cssVar : [colourConfig.cssVar];
        return rawVars.map(v => resolveVariant(v, variant));
    }, [colourConfig.cssVar, variant]);

    const primaryVar = resolvedVars[0];

    const initialValue = useMemo(() => {
        let valInState = null;

        Object.values(styles).forEach(categoryStyles => {
            if (categoryStyles && typeof categoryStyles === 'object' && categoryStyles[primaryVar]) {
                valInState = categoryStyles[primaryVar];
            }
        });

        if (!valInState) {
            valInState = styles[primaryVar];
        }

        return valInState || getDefaultColour(primaryVar, styles);
    }, [primaryVar, styles]);

    const isAuto = colourConfig?.autoGenerated && colours?.[editType]?.autoGenerated;

    return (
        <ContentStyleEditBlock
            editType={editType}
            variant={variant}
        >
            <div className="flexMax">
                {title}
                {isAuto ? <small style={{ marginLeft: '4px', opacity: 0.7 }}>(auto)</small> : ""}
            </div>
            <div className="flex">
                <StyleColourPicker
                    key={`${editType}-${primaryVar}`}
                    defaultValue={initialValue}
                    onChange={(colour) => {
                        const updates = {};
                        resolvedVars.forEach(v => {
                            if (v.endsWith('-RGB')) {
                                updates[v] = colour.rgb().array();
                            } else {
                                updates[v] = colour.hex();
                            }
                        });
                        if (colours) {
                            onChange?.(editType, updates);
                        } else {
                            onChange?.(updates);
                        }
                    }}
                />
            </div>
        </ContentStyleEditBlock>
    );
};

export const ContentStylesDemoSection = props => {
    const {title, children, ...other} = props;

    return (
        <section
            {...other}
            className="ch-s_ed-demo--section"
        >
            {title && (
                <div className="ch-s_ed-demo--section-head">
                    {title}
                </div>
            )}
            <div className="ch-s_ed-demo--section-group">
                {children}
            </div>
        </section>
    )
}

export const ContentStylesDemoSubsection = props => {
    const {title, groupKey, children, ...other} = props;
    const {handleDemoComponentClick} = useContext(ClassStylesEditorContext);

    return (
        <div
            {...other}
            {...(groupKey && {
                onClick: () => handleDemoComponentClick(groupKey)
            })}
            className="ch-s_ed-demo--sub-section"
        >
            {title && (
                <div className="ch-s_ed-demo--sub-section-head">
                    {title}
                </div>
            )}
            <div className="ch-s_ed-demo--sub-section-group">
                {children}
            </div>
        </div>
    )
}

export const ContentStylesDemoSubsectionVariants = props => {
    const {stylesRegistryData, render, baseClassKey = ""} = props;

    if (!stylesRegistryData || !stylesRegistryData.totalVariants || typeof render !== 'function') {
        return null;
    }

    return (
        <>
            {[...Array(stylesRegistryData.totalVariants)].map((_, i) => {
                const variantNum = i + 1;
                const classKey = baseClassKey.replace("*", variantNum);

                return (
                    <React.Fragment key={`demo-${classKey}`}>
                        {render(variantNum, classKey)}
                    </React.Fragment>
                );
            })}
        </>
    );
}